/**
 * The OPT-IN extra email code — client half (Paul's ruling, 2026-08-18).
 *
 * ## What this is, and what it is emphatically not
 *
 * This is NOT a second factor. Supabase knows three factor types — `totp`,
 * `phone`, `webauthn` — and email is not one of them. Nothing in this file
 * calls `supabase.auth.mfa.verify`, so nothing in this file can move a session
 * from aal1 to aal2, and nothing here calls `/v1/auth/refresh-step-up`, so
 * nothing here can stamp `app_metadata.mfa_challenge_at`. Those two claims are
 * the ONLY things the `/admin/*` gate and `requireStepUp()` read.
 *
 * It is an ADDITIONAL challenge a user switches on for themselves. Its only
 * power is to make a sign-in or a step-up HARDER. It can never make one easier.
 *
 * Getting this wrong would re-open the 2026-07-30 P1, where a step-up gate
 * checked that a factor EXISTED rather than that the session had PASSED it. An
 * email code treated as a substitute factor is that bug wearing a hat.
 *
 * ## Where it fires, and why the ordering differs
 *
 * SIGN-IN (`/auth/mfa-verify`): the code is demanded BEFORE the authenticator
 *   code is accepted. That ordering is not cosmetic — it is what makes the
 *   layer fail CLOSED. The session is still aal1 until `mfa.verify()` runs, and
 *   both `/app/_layout` and `/admin/_layout` already bounce an aal1 session
 *   straight back to `/auth/mfa-verify`. So a user who abandons the email step
 *   and types a URL lands right back on it. Had the code been demanded AFTER
 *   the authenticator, the session would already be aal2 and walking away from
 *   the screen would have been a way past the layer.
 *
 * STEP-UP (`useStepUpChallenge`): the code is demanded AFTER the authenticator
 *   or passkey verifies but BEFORE `stampStepUpFreshness()` runs. Same
 *   fail-closed property from the other direction — no stamp, no step-up, and
 *   the gated mutation still 403s. Here "after" is safe precisely because the
 *   thing being withheld is the stamp, not the session.
 *
 * In both places the factor is what carries the session. The code is a gate the
 * user chose to stand in front of it.
 *
 * ## Failure posture: fail OPEN on delivery, CLOSED on a wrong code
 *
 * Paul, 2026-08-18: "A user must never be locked out because an email was
 * slow." An email send now sits on the critical path of every login for anyone
 * opted in, and that path has form — the auth email hook measured 4.06 s on dev
 * against GoTrue's hard 5 s ceiling.
 *
 * So a send that times out, 503s (feature unconfigured) or 502s (ACS degraded)
 * SKIPS the layer and lets the user through on the factor they already hold.
 * The skip is never silent: the caller surfaces it on screen, because a
 * security control that quietly stopped applying is worse than one that never
 * existed.
 *
 * A WRONG CODE is the opposite — a hard failure, no skip, retry only. The
 * client keys off the exact status: 403 means wrong, 503/502/timeout mean
 * unavailable. Collapsing those two would hand an attacker the layer's off
 * switch.
 *
 * ## Honest limit on the preference itself
 *
 * The opt-in lives in Supabase `user_metadata`, which is writable by the user's
 * own session. That is the right home for a user-chosen preference (it follows
 * them across devices with no backend change), but it means the flag is not
 * tamper-proof against someone who already holds a live session. This layer
 * defends the account against someone who has the password and the
 * authenticator but not the mailbox; it does not claim to defend against
 * someone already inside. If Elreign ever needs an ADMIN-ENFORCED email code,
 * that is a different feature with a server-side policy, not this flag.
 */

import { supabase } from './supabase';
import { api, ApiError } from './api';

/** Ceremony the code is bound to, server-side. A sign-in code cannot be
 *  replayed against a step-up challenge and vice versa. */
export type MfaEmailCodePurpose = 'sign-in' | 'step-up';

/**
 * How long we wait for the send before giving up and letting the user through.
 *
 * 8 s. The send is our own Function App calling ACS Email (a POST plus an
 * Operation-Location poll); the comparable measured figure on this stack is the
 * 4.06 s auth-email hook on dev. 8 s clears a normal send roughly twice over
 * while still being a wait a person will sit through without assuming the app
 * has hung. Past it we stop waiting rather than hold a sign-in hostage to ACS.
 */
export const MFA_EMAIL_CODE_SEND_TIMEOUT_MS = 8_000;

/**
 * Verify timeout — longer, and it NEVER passes the user. A verify that times
 * out is ambiguous (the code may or may not have been checked), so the only
 * safe reading is "try again", never "let them in".
 */
export const MFA_EMAIL_CODE_VERIFY_TIMEOUT_MS = 10_000;

/** The `user_metadata` key holding the opt-in. */
export const MFA_EMAIL_CODE_PREF_KEY = 'mfa_email_code';

// -- preference --------------------------------------------------------------

/** PURE — read the opt-in off an already-hydrated user object. */
export function emailCodeEnabledForUser(
  user: { user_metadata?: Record<string, unknown> | null } | null | undefined,
): boolean {
  return user?.user_metadata?.[MFA_EMAIL_CODE_PREF_KEY] === true;
}

/**
 * Is the extra code switched on for the signed-in user?
 *
 * Reads the session rather than the network. A failure here returns false —
 * i.e. the layer does not apply — which is the correct direction: a metadata
 * read that hiccups must not strand someone on a challenge screen for a
 * preference we could not confirm they set.
 */
export async function isEmailCodeEnabled(): Promise<boolean> {
  try {
    const { data } = await supabase.auth.getSession();
    return emailCodeEnabledForUser(data.session?.user);
  } catch {
    return false;
  }
}

/**
 * Turn the extra code on or off. Writes `user_metadata` on the live session, so
 * the choice follows the user to every device they sign in on.
 */
export async function setEmailCodeEnabled(enabled: boolean): Promise<void> {
  const { error } = await supabase.auth.updateUser({
    data: { [MFA_EMAIL_CODE_PREF_KEY]: enabled },
  });
  if (error) throw new Error(error.message);
}

// -- send --------------------------------------------------------------------

export type SendCodeOutcome =
  /** The email is on its way; challenge the user. */
  | { status: 'sent' }
  /**
   * We could not deliver a code. The caller MUST let the user proceed on the
   * factor they already passed, and MUST say so on screen — `reason` is
   * user-facing copy.
   */
  | { status: 'unavailable'; reason: string };

/** Race a promise against a timeout, resolving to `null` if the timeout wins. */
async function withTimeout<T>(work: Promise<T>, ms: number): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), ms);
  });
  try {
    return await Promise.race([work, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Ask the backend to email the user their code.
 *
 * Never throws. Every failure mode collapses to `unavailable` with copy the
 * caller shows the user — see the fail-open note in the file header.
 */
export async function sendEmailCode(purpose: MfaEmailCodePurpose): Promise<SendCodeOutcome> {
  const work = (async (): Promise<SendCodeOutcome> => {
    try {
      await api('/api/v1/auth/mfa-email-code/send', {
        method: 'POST',
        body: { purpose },
        useOperatorSession: true,
      });
      return { status: 'sent' };
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 503) {
        return {
          status: 'unavailable',
          reason: 'The extra email code isn’t available on this environment right now.',
        };
      }
      return {
        status: 'unavailable',
        reason: 'We couldn’t send your email code just now.',
      };
    }
  })();

  const raced = await withTimeout(work, MFA_EMAIL_CODE_SEND_TIMEOUT_MS);
  if (raced === null) {
    // The request may yet succeed server-side — we simply stop waiting on it.
    // The user still holds a verified factor, so waiting longer buys nothing a
    // lockout wouldn't cost more.
    return {
      status: 'unavailable',
      reason: 'Your email code is taking too long to send.',
    };
  }
  return raced;
}

// -- verify ------------------------------------------------------------------

export type VerifyCodeOutcome =
  /** Correct code. Proceed with whatever the caller was gating. */
  | { status: 'ok' }
  /** Wrong or expired. HARD failure — never treat this as a skip. */
  | { status: 'invalid'; message: string }
  /** Transient (network, timeout, 429). Let the user try again; never pass. */
  | { status: 'retry'; message: string }
  /** The feature itself is off server-side — proceed, and say so. */
  | { status: 'unavailable'; reason: string };

/**
 * Check a submitted code.
 *
 * A 200 here grants NOTHING on its own — the endpoint returns a bare
 * `{ ok: true }`, writes no metadata and mints no token. It only tells the
 * caller it may stop withholding what it was already withholding.
 */
export async function verifyEmailCode(
  purpose: MfaEmailCodePurpose,
  code: string,
): Promise<VerifyCodeOutcome> {
  const work = (async (): Promise<VerifyCodeOutcome> => {
    try {
      await api('/api/v1/auth/mfa-email-code/verify', {
        method: 'POST',
        body: { purpose, code },
        useOperatorSession: true,
      });
      return { status: 'ok' };
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        if (e.status === 403) {
          return {
            status: 'invalid',
            message: 'That code is incorrect or has expired. Check your email and try again.',
          };
        }
        if (e.status === 429) {
          return {
            status: 'retry',
            message: 'Too many attempts. Wait a few minutes, then request a new code.',
          };
        }
        if (e.status === 503) {
          return {
            status: 'unavailable',
            reason: 'The extra email code isn’t available on this environment right now.',
          };
        }
      }
      return {
        status: 'retry',
        message: 'We couldn’t check that code just now. Please try again.',
      };
    }
  })();

  const raced = await withTimeout(work, MFA_EMAIL_CODE_VERIFY_TIMEOUT_MS);
  if (raced === null) {
    // Ambiguous — the code may or may not have been checked. The only safe
    // reading is "unknown", and unknown is never a pass.
    return {
      status: 'retry',
      message: 'That took too long to check. Please try again.',
    };
  }
  return raced;
}
